import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type SspRedactionLlmCheck,
  type SspRedactionPreview,
  type SspRedactionPreviewPage,
  type SspRedactionRect,
} from '@/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertTriangle,
  Download,
  FileLock2,
  FileText,
  Loader2,
  MousePointer2,
  Pencil,
  Save,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react'

const defaultCategories = [
  {
    id: 'release_markings',
    label: 'Release markings',
    description: 'Classification, CUI, NOFORN, REL TO, export-control, proprietary, or handling caveats.',
  },
  {
    id: 'organizations_people',
    label: 'Organizations and people',
    description: 'Named organizations, offices, teams, people, contact IDs, emails, and phone numbers.',
  },
  {
    id: 'system_identifiers',
    label: 'System identifiers',
    description: 'System, application, service, asset, host, repository, acronym, alias, and ID values.',
  },
  {
    id: 'architecture_boundary',
    label: 'Architecture and boundaries',
    description: 'Boundary descriptions, data flows, interconnections, environments, network paths, and external services.',
  },
  {
    id: 'identity_access',
    label: 'Identity and access',
    description: 'Privileged roles, service accounts, emergency access, MFA, access reviews, and authorization workflows.',
  },
  {
    id: 'crypto_secrets',
    label: 'Crypto and secrets',
    description: 'Cryptographic design, TLS details, key management, tokens, credentials, API keys, and certificates.',
  },
]

type DraftRect = {
  x: number
  y: number
  width: number
  height: number
}

function parseError(error: unknown) {
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message)
      return parsed.detail || error.message
    } catch {
      return error.message
    }
  }
  return 'Request failed.'
}

export function SSPRedactorPage() {
  const queryClient = useQueryClient()
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [redactionName, setRedactionName] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>(defaultCategories.map(category => category.id))
  const [manualDescription, setManualDescription] = useState('')
  const [selectedConnectionId, setSelectedConnectionId] = useState('')
  const [connectionName, setConnectionName] = useState('Local SSP reviewer')
  const [llmUrl, setLlmUrl] = useState('')
  const [llmModel, setLlmModel] = useState('')
  const [llmApiKey, setLlmApiKey] = useState('')
  const [llmTimeout, setLlmTimeout] = useState(240)
  const [connectionStatus, setConnectionStatus] = useState<SspRedactionLlmCheck | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [isSavingConnection, setIsSavingConnection] = useState(false)
  const [isCheckingConnection, setIsCheckingConnection] = useState(false)
  const [isDeletingConnection, setIsDeletingConnection] = useState(false)
  const [ocr, setOcr] = useState('auto')
  const [jobId, setJobId] = useState<string | null>(null)
  const [runName, setRunName] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [preview, setPreview] = useState<SspRedactionPreview | null>(null)
  const [activePageNumber, setActivePageNumber] = useState(1)
  const [rects, setRects] = useState<SspRedactionRect[]>([])
  const [draftRect, setDraftRect] = useState<DraftRect | null>(null)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const previewSectionRef = useRef<HTMLDivElement | null>(null)

  const { data: options } = useQuery({
    queryKey: ['complyiq', 'ssp-redactions', 'options'],
    queryFn: () => api.complyiq.sspRedactions.options(),
    retry: false,
  })

  const { data: job } = useQuery({
    queryKey: ['complyiq', 'ssp-redactions', 'job', jobId],
    enabled: Boolean(jobId),
    queryFn: () => api.complyiq.sspRedactions.job(jobId as string),
    refetchInterval: query => {
      const status = query.state.data?.status
      return status === 'complete' || status === 'error' ? false : 1000
    },
  })

  const categories = options?.categories?.length ? options.categories : defaultCategories
  const savedConnections = options?.saved_llm_connections ?? []
  const selectedConnection = savedConnections.find(connection => connection.config_id === selectedConnectionId)
  const llmReady = Boolean(selectedConnectionId && connectionStatus?.ok)
  const activePage = preview?.pages.find(page => page.page === activePageNumber) ?? preview?.pages[0] ?? null
  const pageRects = rects.filter(rect => rect.page === activePageNumber)

  useEffect(() => {
    if (!selectedConnectionId && savedConnections.length) {
      const firstConnection = savedConnections[0]
      setSelectedConnectionId(firstConnection.config_id)
      setConnectionName(firstConnection.display_name)
      setLlmUrl(firstConnection.url)
      setLlmModel(firstConnection.model)
      setLlmTimeout(firstConnection.timeout_seconds || 240)
    }
  }, [savedConnections, selectedConnectionId])

  useEffect(() => {
    setConnectionStatus(null)
    setConnectionError(null)
  }, [selectedConnectionId, llmUrl])

  useEffect(() => {
    if (!job || job.status !== 'complete') {
      return
    }
    setRunName(job.run_name)
    api.complyiq.sspRedactions.preview(job.run_name)
      .then(data => {
        setPreview(data)
        setActivePageNumber(data.pages[0]?.page ?? 1)
        setRects(data.pages.flatMap(page => page.rects))
        requestAnimationFrame(() => {
          if (typeof previewSectionRef.current?.scrollIntoView === 'function') {
            previewSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      })
      .catch(error => setStartError(parseError(error)))
  }, [job])

  function toggleCategory(categoryId: string) {
    setSelectedCategories(current => (
      current.includes(categoryId)
        ? current.filter(item => item !== categoryId)
        : [...current, categoryId]
    ))
  }

  async function startRedaction() {
    if (!sourceFile) {
      setStartError('Select a source PDF before running redaction.')
      return
    }
    if (!llmReady) {
      setStartError('Save and check an LLM connection before running redaction.')
      return
    }
    setIsStarting(true)
    setStartError(null)
    setSaveStatus(null)
    setPreview(null)
    setRects([])
    try {
      const nextJob = await api.complyiq.sspRedactions.start({
        sourceFile,
        redactionName,
        redactTypes: selectedCategories,
        manualDescription,
        llmConfigId: selectedConnectionId,
        llmModel,
        llmTimeout,
        ocr,
      })
      setJobId(nextJob.job_id)
      setRunName(nextJob.run_name)
    } catch (error) {
      setStartError(parseError(error))
    } finally {
      setIsStarting(false)
    }
  }

  async function saveConnection() {
    setIsSavingConnection(true)
    setConnectionError(null)
    setConnectionStatus(null)
    try {
      const result = await api.complyiq.sspRedactions.saveConnection({
        displayName: connectionName,
        url: llmUrl,
        model: llmModel,
        apiKeyValue: llmApiKey,
        timeoutSeconds: llmTimeout,
        verifyTls: true,
      })
      setSelectedConnectionId(result.connection.config_id)
      setLlmApiKey('')
      await queryClient.invalidateQueries({ queryKey: ['complyiq', 'ssp-redactions', 'options'] })
    } catch (error) {
      setConnectionError(parseError(error))
    } finally {
      setIsSavingConnection(false)
    }
  }

  async function checkConnection() {
    setIsCheckingConnection(true)
    setConnectionError(null)
    try {
      const result = await api.complyiq.sspRedactions.checkConnection(
        selectedConnectionId
          ? { configId: selectedConnectionId, model: llmModel }
          : {
              displayName: connectionName,
              url: llmUrl,
              model: llmModel,
              apiKeyValue: llmApiKey,
              timeoutSeconds: llmTimeout,
              verifyTls: true,
            }
      )
      setConnectionStatus(result)
      if (!result.ok) {
        setConnectionError(result.message)
      }
      if (result.model) {
        setLlmModel(result.model)
      }
    } catch (error) {
      setConnectionStatus(null)
      setConnectionError(parseError(error))
    } finally {
      setIsCheckingConnection(false)
    }
  }

  async function deleteConnection() {
    if (!selectedConnectionId) {
      return
    }
    const connectionToDelete = savedConnections.find(connection => connection.config_id === selectedConnectionId)
    if (!connectionToDelete) {
      return
    }
    if (!window.confirm(`Delete saved connection "${connectionToDelete.display_name}"?`)) {
      return
    }
    setIsDeletingConnection(true)
    setConnectionError(null)
    setConnectionStatus(null)
    try {
      await api.complyiq.sspRedactions.deleteConnection(selectedConnectionId)
      setSelectedConnectionId('')
      setConnectionName('Local SSP reviewer')
      setLlmUrl('')
      setLlmModel('')
      setLlmApiKey('')
      await queryClient.invalidateQueries({ queryKey: ['complyiq', 'ssp-redactions', 'options'] })
    } catch (error) {
      setConnectionError(parseError(error))
    } finally {
      setIsDeletingConnection(false)
    }
  }

  function pointFromEvent(event: React.PointerEvent<HTMLDivElement>) {
    const page = activePage
    const bounds = canvasRef.current?.getBoundingClientRect()
    if (!page || !bounds) {
      return null
    }
    return {
      x: Math.max(0, Math.min(page.width, ((event.clientX - bounds.left) / bounds.width) * page.width)),
      y: Math.max(0, Math.min(page.height, ((event.clientY - bounds.top) / bounds.height) * page.height)),
    }
  }

  function startDraw(event: React.PointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).dataset.redactionRect === 'true') {
      return
    }
    const point = pointFromEvent(event)
    if (!point) {
      return
    }
    setDragStart(point)
    setDraftRect({ x: point.x, y: point.y, width: 0, height: 0 })
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  function continueDraw(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragStart) {
      return
    }
    const point = pointFromEvent(event)
    if (!point) {
      return
    }
    setDraftRect({
      x: Math.min(dragStart.x, point.x),
      y: Math.min(dragStart.y, point.y),
      width: Math.abs(point.x - dragStart.x),
      height: Math.abs(point.y - dragStart.y),
    })
  }

  function finishDraw() {
    if (draftRect && draftRect.width >= 2 && draftRect.height >= 2) {
      setRects(current => [
        ...current,
        {
          id: `manual-${Date.now()}`,
          page: activePageNumber,
          x: Number(draftRect.x.toFixed(2)),
          y: Number(draftRect.y.toFixed(2)),
          width: Number(draftRect.width.toFixed(2)),
          height: Number(draftRect.height.toFixed(2)),
          source: 'manual_visual',
        },
      ])
    }
    setDragStart(null)
    setDraftRect(null)
  }

  async function saveRedactions() {
    if (!runName) {
      return
    }
    setIsSaving(true)
    setSaveStatus(null)
    try {
      const result = await api.complyiq.sspRedactions.savePreview(runName, rects)
      setSaveStatus(`Saved ${result.saved_count} redaction boxes and rebuilt redacted.pdf.`)
    } catch (error) {
      setSaveStatus(parseError(error))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/20">
            <FileLock2 className="h-5 w-5 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-display font-bold">SSP Foreign Disclosure Redactor</h1>
          <p className="mt-2 max-w-3xl text-muted-foreground">
            Prepare SSP PDFs for foreign disclosure review with local redaction processing and visual reviewer control.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="low" className="gap-1">
            <ShieldCheck className="h-3 w-3" />
            Source preserved
          </Badge>
          <Badge variant="info" className="gap-1">
            <MousePointer2 className="h-3 w-3" />
            Interactive review
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)] xl:items-start">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="h-4 w-4" />
                Source
              </CardTitle>
              <CardDescription>Upload the SSP PDF or supported source document to create a redaction run.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-medium">Source PDF</span>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  onChange={event => setSourceFile(event.target.files?.[0] ?? null)}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium">Run name</span>
                <input
                  value={redactionName}
                  onChange={event => setRedactionName(event.target.value)}
                  placeholder="Leave blank for the next redaction number"
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium">OCR</span>
                <select
                  value={ocr}
                  onChange={event => setOcr(event.target.value)}
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                >
                  <option value="auto">Auto</option>
                  <option value="required">Required</option>
                  <option value="off">Off</option>
                </select>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">LLM Connection</CardTitle>
              <CardDescription>Save and check a local reviewer connection before running redaction.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_168px] sm:items-end">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">Saved connection</span>
                  <select
                    value={selectedConnectionId}
                    onChange={event => {
                      const nextId = event.target.value
                      const connection = savedConnections.find(item => item.config_id === nextId)
                      setSelectedConnectionId(nextId)
                      if (connection) {
                        setConnectionName(connection.display_name)
                        setLlmUrl(connection.url)
                        setLlmModel(connection.model)
                        setLlmTimeout(connection.timeout_seconds || 240)
                      }
                    }}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                  >
                    <option value="">New connection</option>
                    {savedConnections.map(connection => (
                      <option key={connection.config_id} value={connection.config_id}>
                        {connection.display_name}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={deleteConnection}
                  disabled={!selectedConnectionId || isDeletingConnection}
                  className="h-10 border-destructive/50 bg-destructive/10 font-medium text-destructive shadow-sm hover:bg-destructive/15 hover:text-destructive"
                >
                  {isDeletingConnection ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                  Delete Saved
                </Button>
              </div>
              <label className="block">
                <span className="mb-2 block text-sm font-medium">Connection name</span>
                <input
                  value={connectionName}
                  onChange={event => {
                    setSelectedConnectionId('')
                    setConnectionName(event.target.value)
                  }}
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">API endpoint</span>
                  <input
                    value={llmUrl}
                    onChange={event => {
                      setSelectedConnectionId('')
                      setLlmUrl(event.target.value)
                    }}
                    placeholder="http://127.0.0.1:8000/v1/chat/completions"
                    className="h-10 w-full rounded-md border border-border bg-muted/30 px-3 text-xs placeholder:text-muted-foreground/75"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">Model name</span>
                  <input
                    value={llmModel}
                    onChange={event => {
                      setConnectionStatus(null)
                      setLlmModel(event.target.value)
                    }}
                    placeholder="Example: gpt-oss:20b, llama3.1, local-model"
                    className="h-10 w-full rounded-md border border-border bg-muted/30 px-3 text-xs placeholder:text-muted-foreground/75"
                  />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Endpoint must be an HTTP(S) OpenAI-compatible chat completions endpoint. Use an approved endpoint for SSP content.
              </p>
              <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">API key</span>
                  <input
                    value={llmApiKey}
                    onChange={event => {
                      setSelectedConnectionId('')
                      setLlmApiKey(event.target.value)
                    }}
                    type="password"
                    placeholder="Only if your local endpoint requires one"
                    className="h-10 w-full rounded-md border border-border bg-muted/30 px-3 text-xs placeholder:text-muted-foreground/75"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">Timeout</span>
                  <input
                    value={llmTimeout}
                    onChange={event => setLlmTimeout(Number(event.target.value) || 240)}
                    type="number"
                    min={1}
                    className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                    aria-label="LLM timeout seconds"
                  />
                </label>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button variant="outline" onClick={saveConnection} disabled={isSavingConnection || !connectionName || !llmUrl || !llmModel}>
                  {isSavingConnection ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Connection
                </Button>
                <Button variant="outline" onClick={checkConnection} disabled={isCheckingConnection || (!selectedConnectionId && !connectionName)}>
                  {isCheckingConnection ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Check Connection
                </Button>
              </div>
              {selectedConnection && (
                <p className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                  Active connection: {selectedConnection.display_name}
                </p>
              )}
              {connectionStatus?.ok && (
                <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-400">
                  {connectionStatus.message}
                </p>
              )}
              {connectionError && (
                <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {connectionError}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-primary/20 bg-gradient-to-r from-card via-card to-primary/5">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle className="text-base">Run Redaction</CardTitle>
                  <CardDescription>Start the SSP redaction pass after the source, categories, and connection are ready.</CardDescription>
                </div>
                <Button
                  className="min-w-[220px] self-start"
                  onClick={startRedaction}
                  disabled={isStarting || !sourceFile || selectedCategories.length === 0 || !llmReady}
                >
                  {isStarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileLock2 className="h-4 w-4" />}
                  Run Redaction
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant={sourceFile ? 'low' : 'outline'}>{sourceFile ? 'Source loaded' : 'Source required'}</Badge>
                <Badge variant={selectedCategories.length > 0 ? 'info' : 'outline'}>
                  {selectedCategories.length} categories selected
                </Badge>
                <Badge variant={llmReady ? 'low' : 'outline'}>
                  {llmReady ? 'Connection checked' : 'Connection check required'}
                </Badge>
              </div>
              {job && (
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <span>{job.phase || job.message}</span>
                    <span className="font-medium">{job.progress_percent}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-background">
                    <div className="h-full bg-primary" style={{ width: `${job.progress_percent}%` }} />
                  </div>
                  {job.error && <p className="mt-2 text-sm text-destructive">{job.error}</p>}
                </div>
              )}
              {startError && (
                <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {startError}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Redaction Categories</CardTitle>
              <CardDescription>Choose the focus areas to redact or review before running the pass.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 lg:grid-cols-2">
                {categories.map(category => (
                  <label key={category.id} className="flex cursor-pointer gap-3 rounded-md border border-border p-2.5 hover:bg-muted/30">
                    <Checkbox
                      checked={selectedCategories.includes(category.id)}
                      onCheckedChange={() => toggleCategory(category.id)}
                      aria-label={category.label}
                    />
                    <span>
                      <span className="block text-sm font-medium">{category.label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{category.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <textarea
                value={manualDescription}
                onChange={event => setManualDescription(event.target.value)}
                placeholder="Optional reviewer instructions"
                className="min-h-[76px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <div ref={previewSectionRef}>
        <Card className="min-h-[720px]">
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-4 w-4" />
                  Interactive Preview
                </CardTitle>
                <CardDescription>Click a redaction bar to remove it, or drag on the page to draw a new box.</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={saveRedactions} disabled={!runName || rects.length === 0 || isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Redactions
                </Button>
                <Button asChild variant="outline" disabled={!runName}>
                  <a href={runName ? api.complyiq.sspRedactions.downloadUrl(runName, 'redacted.pdf') : undefined}>
                    <Download className="h-4 w-4" />
                    redacted.pdf
                  </a>
                </Button>
                <Button asChild variant="outline" disabled={!runName}>
                  <a href={runName ? api.complyiq.sspRedactions.downloadUrl(runName, 'redaction_report.pdf') : undefined}>
                    <Download className="h-4 w-4" />
                    report.pdf
                  </a>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview && activePage ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {preview.pages.map(page => (
                      <Button
                        key={page.page}
                        variant={page.page === activePageNumber ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setActivePageNumber(page.page)}
                      >
                        Page {page.page}
                      </Button>
                    ))}
                  </div>
                  <Badge variant="pending" className="gap-1">
                    <Pencil className="h-3 w-3" />
                    {rects.length} boxes
                  </Badge>
                </div>

                <PreviewCanvas
                  page={activePage}
                  runName={runName || preview.run_name}
                  rects={pageRects}
                  draftRect={draftRect}
                  canvasRef={canvasRef}
                  onPointerDown={startDraw}
                  onPointerMove={continueDraw}
                  onPointerUp={finishDraw}
                  onRemoveRect={rectId => setRects(current => current.filter(rect => rect.id !== rectId))}
                />
              </>
            ) : (
              <div className="flex min-h-[560px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/10 text-center">
                <FileLock2 className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-medium">No preview loaded</p>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  Run a redaction to render the PDF preview and edit the proposed redaction boxes.
                </p>
              </div>
            )}

            {saveStatus && (
              <p className="rounded-md border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                {saveStatus}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function PreviewCanvas({
  page,
  runName,
  rects,
  draftRect,
  canvasRef,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onRemoveRect,
}: {
  page: SspRedactionPreviewPage
  runName: string
  rects: SspRedactionRect[]
  draftRect: DraftRect | null
  canvasRef: React.MutableRefObject<HTMLDivElement | null>
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void
  onPointerUp: () => void
  onRemoveRect: (rectId: string) => void
}) {
  return (
    <div className="overflow-auto rounded-md border border-border bg-background p-3">
      <div
        ref={canvasRef}
        className="relative mx-auto cursor-crosshair overflow-hidden rounded-sm bg-muted shadow-sm"
        style={{ width: 'min(100%, 860px)', aspectRatio: `${page.width} / ${page.height}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="application"
        aria-label={`PDF redaction preview page ${page.page}`}
      >
        <img
          src={api.complyiq.sspRedactions.previewPageUrl(runName, page.page)}
          alt={`PDF page ${page.page}`}
          className="h-full w-full select-none object-contain"
          draggable={false}
        />
        {rects.map(rect => (
          <button
            key={rect.id}
            type="button"
            data-redaction-rect="true"
            aria-label={`Remove redaction ${rect.id}`}
            className="absolute border border-black bg-black/90 text-white shadow-sm transition hover:bg-destructive/90"
            style={{
              left: `${(rect.x / page.width) * 100}%`,
              top: `${(rect.y / page.height) * 100}%`,
              width: `${(rect.width / page.width) * 100}%`,
              height: `${(rect.height / page.height) * 100}%`,
            }}
            onClick={event => {
              event.stopPropagation()
              onRemoveRect(rect.id)
            }}
          >
            <X className="m-auto h-3 w-3 opacity-0 hover:opacity-100" />
          </button>
        ))}
        {draftRect && (
          <div
            className="absolute border border-emerald-300 bg-emerald-400/30"
            style={{
              left: `${(draftRect.x / page.width) * 100}%`,
              top: `${(draftRect.y / page.height) * 100}%`,
              width: `${(draftRect.width / page.width) * 100}%`,
              height: `${(draftRect.height / page.height) * 100}%`,
            }}
          />
        )}
      </div>
    </div>
  )
}
